import { alpha, createTheme } from "@mui/material/styles";

/**
 * Dashboard colours, taken from the dark espresso and gold sections of the
 * public site so the admin area feels like the same product.
 */
export const DASHBOARD_COLORS = {
  gold: "#c7a45d",
  goldLight: "#ead7a7",
  goldDeep: "#aa8238",
  ink: "#15120f",
  surface: "#1d1915",
  paper: "#252018",
  raised: "#2e281f",
  text: "#f5efe3",
  textMuted: "rgba(245, 239, 227, 0.62)",
  line: "rgba(234, 215, 167, 0.12)",
};

const c = DASHBOARD_COLORS;

const dashboardTheme = createTheme({
  palette: {
    mode: "dark",
    primary: { main: c.gold, light: c.goldLight, dark: c.goldDeep, contrastText: c.ink },
    secondary: { main: "#d9a58b", contrastText: c.ink },
    // Status colours softened to sit beside the gold rather than fight it.
    success: { main: "#8fbf8a" },
    error: { main: "#e07a6b" },
    warning: { main: "#e3a857" },
    info: { main: "#8db4cc" },
    background: { default: c.surface, paper: c.paper },
    text: { primary: c.text, secondary: c.textMuted },
    divider: c.line,
    action: {
      hover: alpha(c.gold, 0.08),
      selected: alpha(c.gold, 0.16),
    },
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: `"DM Sans", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
    h1: { fontFamily: `"Playfair Display", Georgia, serif`, fontWeight: 500 },
    h2: { fontFamily: `"Playfair Display", Georgia, serif`, fontWeight: 500 },
    h3: { fontFamily: `"Playfair Display", Georgia, serif`, fontWeight: 500 },
    h4: { fontFamily: `"Playfair Display", Georgia, serif`, fontWeight: 500 },
    h5: { fontFamily: `"Playfair Display", Georgia, serif`, fontWeight: 500 },
    h6: { fontWeight: 600 },
    button: { fontWeight: 700, textTransform: "none" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: c.surface },
        "*::-webkit-scrollbar": { width: 10, height: 10 },
        "*::-webkit-scrollbar-thumb": { backgroundColor: alpha(c.goldLight, 0.16), borderRadius: 999 },
        "*::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      },
    },
    // Dark-mode MUI adds a white overlay gradient to raised paper; keep surfaces flat and warm instead.
    MuiPaper: { styleOverrides: { root: { backgroundImage: "none" } } },
    MuiCard: { styleOverrides: { root: { border: `1px solid ${c.line}` } } },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: 999, paddingInline: 18 },
        containedPrimary: {
          background: `linear-gradient(135deg, ${c.goldDeep}, ${c.gold})`,
          color: "#fff",
          "&:hover": { background: `linear-gradient(135deg, ${c.goldDeep}, ${c.goldDeep})` },
        },
      },
    },
    MuiChip: { styleOverrides: { root: { fontWeight: 600 } } },
    MuiTab: { styleOverrides: { root: { textTransform: "none", fontWeight: 600 } } },
    MuiTooltip: {
      styleOverrides: { tooltip: { backgroundColor: c.raised, border: `1px solid ${c.line}`, color: c.text } },
    },
    MuiDialog: { styleOverrides: { paper: { border: `1px solid ${c.line}` } } },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(c.goldLight, 0.4) },
        },
        notchedOutline: { borderColor: alpha(c.goldLight, 0.2) },
      },
    },
  },
});

export default dashboardTheme;
